import type { RosterFilters } from "#shared/reviewee-filters/rosterFilters.ts";
import { CycleReviewStatusFilters, CycleRevieweeExceptions } from "#shared/reviewee-filters/rosterFilters.ts";
import { CycleDeliveryStates, CycleRevieweeStates, type CycleReviewType } from "#shared/reviewCycleSchemas.ts";
import { parseAsArrayOf, parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useCallback, useMemo } from "react";

const RosterFilterKinds = ["selfStatus", "managerStatus", "peerStatus", "upwardStatus", "delivery", "exceptions", "state"] as const;

export type RosterFilterKind = (typeof RosterFilterKinds)[number];

export const ROSTER_STATUS_KIND_BY_REVIEW_TYPE = {
    self: "selfStatus",
    manager: "managerStatus",
    peer: "peerStatus",
    upward: "upwardStatus"
} as const satisfies Record<CycleReviewType, RosterFilterKind>;

const rosterFilterQuerySchema = {
    q: parseAsString.withDefault(""),
    selfStatus: parseAsArrayOf(parseAsStringLiteral(CycleReviewStatusFilters)).withDefault([]),
    managerStatus: parseAsArrayOf(parseAsStringLiteral(CycleReviewStatusFilters)).withDefault([]),
    peerStatus: parseAsArrayOf(parseAsStringLiteral(CycleReviewStatusFilters)).withDefault([]),
    upwardStatus: parseAsArrayOf(parseAsStringLiteral(CycleReviewStatusFilters)).withDefault([]),
    delivery: parseAsArrayOf(parseAsStringLiteral(CycleDeliveryStates)).withDefault([]),
    exceptions: parseAsArrayOf(parseAsStringLiteral(CycleRevieweeExceptions)).withDefault([]),
    state: parseAsArrayOf(parseAsStringLiteral(CycleRevieweeStates)).withDefault([])
};

const FILTER_QUERY_RESET: Record<RosterFilterKind, Record<string, null>> = {
    selfStatus: { selfStatus: null },
    managerStatus: { managerStatus: null },
    peerStatus: { peerStatus: null },
    upwardStatus: { upwardStatus: null },
    delivery: { delivery: null },
    exceptions: { exceptions: null },
    state: { state: null }
};

const CLEARED_FILTERS = Object.fromEntries(Object.keys(rosterFilterQuerySchema).map(key => [key, null]));

const queryStateOptions = { history: "replace" as const, clearOnDefault: true, shallow: false };

export function useRosterFilters() {
    const [{ q, selfStatus, managerStatus, peerStatus, upwardStatus, delivery, exceptions, state }, setFilters] = useQueryStates(rosterFilterQuerySchema, queryStateOptions);

    const rosterFilters = useMemo<RosterFilters>(
        () => ({
            searchTerm: q.trim() || undefined,
            selfReviewStatuses: selfStatus.length ? selfStatus : undefined,
            managerReviewStatuses: managerStatus.length ? managerStatus : undefined,
            peerReviewStatuses: peerStatus.length ? peerStatus : undefined,
            upwardReviewStatuses: upwardStatus.length ? upwardStatus : undefined,
            deliveryStates: delivery.length ? delivery : undefined,
            exceptions: exceptions.length ? exceptions : undefined,
            revieweeStates: state.length ? state : undefined
        }),
        [q, selfStatus, managerStatus, peerStatus, upwardStatus, delivery, exceptions, state]
    );

    const statusesByKind = useMemo(() => ({ selfStatus, managerStatus, peerStatus, upwardStatus }), [selfStatus, managerStatus, peerStatus, upwardStatus]);

    const removeFilter = useCallback((kind: RosterFilterKind) => void setFilters(FILTER_QUERY_RESET[kind]), [setFilters]);
    const clearFilters = useCallback(() => void setFilters(CLEARED_FILTERS), [setFilters]);

    const hasActiveFilters = !!rosterFilters.searchTerm || [selfStatus, managerStatus, peerStatus, upwardStatus, delivery, exceptions, state].some(values => values.length > 0);

    return { searchTerm: q, statusesByKind, delivery, exceptions, state, rosterFilters, hasActiveFilters, setFilters, removeFilter, clearFilters };
}
